/* Firebase 초기화 — 인증과 SQL Connect 연결.
 *
 * **누가 요청했는지는 서버가 토큰에서 꺼낸다.** 프론트는 uid 를 만지지도, 보내지도
 * 않는다 — mutations.gql 이 전부 `{_expr: "auth.uid"}` 로 채운다. 그래서 여기서 할
 * 일은 "로그인시키는 것"뿐이고, 남의 기록에 손대는 요청은 만들어낼 수가 없다.
 *
 * 로컬과 배포의 갈림길은 **프로젝트 ID 하나**다. 비어 있거나 'demo-' 로 시작하면
 * 에뮬레이터에 붙고, 로그인도 고정 개발계정으로 조용히 끝난다. 실제 프로젝트 ID 를
 * 넣으면 Microsoft(Entra ID) 팝업으로 바뀐다.
 *
 * 이게 옛 auth.py 의 ALLOW_DEV_AUTH 자리인데 기본값이 반대다 — 그때는 켜진 채로
 * 배포하면 그대로 열렸지만, 지금은 실제 프로젝트 ID 를 넣어야만 실제 인증이 켜지고
 * 에뮬레이터 주소(localhost)는 배포에서 닿지도 않는다. 안전한 쪽이 기본값이어야 한다.
 */

import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword, connectAuthEmulator, getAuth, OAuthProvider,
  onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup,
  signOut as fbSignOut,
} from "firebase/auth";
import { connectDataConnectEmulator, getDataConnect } from "firebase/data-connect";

import { connectorConfig } from "@semichelin/dataconnect";

const env = import.meta.env;

/* 배포 때 채운다. 콘솔 > 프로젝트 설정 > 내 앱 > SDK 설정에 그대로 있는 값들이다.
   비워두면 아래에서 데모 프로젝트로 떨어져 에뮬레이터를 본다. */
const PROJECT_ID = env.VITE_FIREBASE_PROJECT_ID || "demo-semichelin";

/** 프로젝트 ID 가 'demo-' 면 Firebase 가 실서비스 호출을 아예 막는다. 로컬 전용 표식이다. */
export const USE_EMULATOR = PROJECT_ID.startsWith("demo-");

const app = initializeApp({
  projectId: PROJECT_ID,
  apiKey: env.VITE_FIREBASE_API_KEY || "demo-api-key",
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || `${PROJECT_ID}.firebaseapp.com`,
  appId: env.VITE_FIREBASE_APP_ID || "demo-app-id",
});

const auth = getAuth(app);
export const dc = getDataConnect(app, connectorConfig);

if (USE_EMULATOR) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectDataConnectEmulator(dc, "127.0.0.1", 9399, false);
}

/* 로컬 개발용 고정 계정.
   팝업을 띄우지 않는 이유가 두 가지다 — 새로고침마다 계정을 고르게 하면 개발이
   느려지고, Playwright(tools/e2e.py)가 팝업 창을 넘어가야 해서 검사가 취약해진다.
   익명 로그인으로는 안 된다: 연산이 전부 @auth(level: USER) 라 익명은 거부된다.
   이 계정은 에뮬레이터 안에만 있고 프로세스를 내리면 사라진다. */
const DEV_EMAIL = "dev@semiai.ai";
const DEV_PASSWORD = "semichelin-dev";

async function signInDev() {
  try {
    return await signInWithEmailAndPassword(auth, DEV_EMAIL, DEV_PASSWORD);
  } catch {
    return await createUserWithEmailAndPassword(auth, DEV_EMAIL, DEV_PASSWORD);
  }
}

/* 실사용 로그인은 Microsoft(Entra ID) 회사 계정이다 — Teams·Outlook 과 같은 계정.
 *
 * tenant 를 넘기는 것이 **사내 제한의 본체**다. 이 값이 있으면 Azure 가 그 테넌트의
 * 계정만 인증하므로, 우리 쪽에 이메일 도메인 검사를 따로 짤 필요가 없다.
 * (구글의 hd 파라미터는 계정 선택창 힌트일 뿐 우회 가능해서 같은 역할을 못 한다.)
 *
 * 그래서 값이 없으면 **로그인시키지 않고 던진다.** 비워두면 'common' 과 같아져
 * 아무 마이크로소프트 계정이나 들어오는데, 그건 조용히 열려 있는 상태다.
 * 이 프로젝트는 안전한 쪽을 기본값으로 둔다 — 시끄럽게 죽는 편이 낫다.
 */
const MS_TENANT_ID = (env.VITE_MS_TENANT_ID || "").trim();

function microsoftProvider() {
  if (!MS_TENANT_ID) {
    throw new Error(
      "VITE_MS_TENANT_ID 가 비어 있습니다. Entra 테넌트 ID 를 넣지 않으면 사내 계정 제한이 걸리지 않습니다.");
  }
  const provider = new OAuthProvider("microsoft.com");
  provider.setCustomParameters({ tenant: MS_TENANT_ID });
  /* 프로필 사진을 읽으려면 필요하다. Entra 는 사진을 토큰에 담지 않아서
     Graph 를 따로 불러야 하고, 그러려면 이 범위가 있어야 한다.
     사내 앱 등록에 기본으로 들어 있는 권한이라 대개 추가 동의가 안 뜬다. */
  provider.addScope("User.Read");
  return provider;
}

/* 프로필 사진 캐시.
 *
 * **사진은 로그인하는 순간에만 가져올 수 있다.** Graph 호출에 쓸 액세스 토큰이
 * 그때만 손에 들어오고, 새로고침 뒤에는 Firebase 세션만 남는다. 그래서 한 번
 * 받아서 data URL 로 넣어둔다.
 *
 * localStorage 를 쓰는 게 리뷰를 DB 로 옮긴 것과 모순처럼 보이지만, 이건
 * **기록이 아니라 캐시**다. 지워져도 다음 로그인에 다시 채워지고, 없으면
 * 이니셜로 떨어진다. DB에 넣을 만한 값이 아니다.
 */
const PHOTO_KEY = (uid) => `semichelin:photo:${uid}`;

async function cachePhoto(cred) {
  const token = OAuthProvider.credentialFromResult(cred)?.accessToken;
  if (!token) return;
  try {
    const res = await fetch("https://graph.microsoft.com/v1.0/me/photos/96x96/$value",
                            { headers: { Authorization: `Bearer ${token}` } });
    /* 사진을 안 올린 사람은 404 다. 오류가 아니라 정상 경로다. */
    if (!res.ok) return;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    localStorage.setItem(PHOTO_KEY(cred.user.uid), dataUrl);
  } catch {
    /* 사진은 없어도 되는 것이다. 여기서 던지면 사진 때문에 로그인이 실패한다. */
  }
}

/** 캐시된 프로필 사진. 없으면 null — 그때는 이니셜을 쓴다. */
export function photoOf(user) {
  if (!user) return null;
  try { return localStorage.getItem(PHOTO_KEY(user.uid)); } catch { return null; }
}

/** 로그인 상태 구독. 해제 함수를 돌려준다. 세션은 브라우저에 남으므로 새로고침해도 유지된다. */
export const onUser = (cb) => onAuthStateChanged(auth, cb);

/** 지금 로그인된 사용자. 없으면 null. */
export const currentUser = () => auth.currentUser;

/* **반드시 사용자의 클릭에서 불러야 한다.**
   페이지 로드 중에 팝업을 열면 브라우저가 막는다. 예전에는 이펙트 안에서 자동으로
   불렀는데, 팝업이 조용히 차단되고 화면에는 "기록을 불러오지 못했습니다"만 떠서
   원인이 안 보였다. 각자 브라우저 설정을 바꾸게 할 수는 없으니 버튼으로 옮겼다.
   (UserButton.jsx) */
export async function signIn() {
  if (USE_EMULATOR) return (await signInDev()).user;
  const cred = await signInWithPopup(auth, microsoftProvider());
  /* 여기서 기다리는 이유: 로그인이 끝난 뒤에 사진을 채우면 화면이 이니셜로 한 번
     그려졌다가 바뀐다. 버튼은 이미 '로그인 중' 상태라 이 한 번의 왕복이 안 보인다. */
  await cachePhoto(cred);
  return cred.user;
}

/* 로그아웃하면 사진 캐시도 지운다. 같은 브라우저를 다른 사람이 이어 쓸 때
   앞사람 얼굴이 남아 있으면 안 된다. 다시 로그인하면 어차피 다시 받는다. */
export async function signOut() {
  const uid = auth.currentUser?.uid;
  await fbSignOut(auth);
  if (uid) { try { localStorage.removeItem(PHOTO_KEY(uid)); } catch { /* 캐시다 */ } }
}

/* 사용자가 팝업을 그냥 닫은 것. 실패가 아니므로 조용히 넘긴다.
   코드에 'cancel' 이 들어가는지로 보면 안 된다 — 닫기는
   auth/popup-closed-by-user 라서 그 검사에 안 걸린다. */
export const isSignInCancel = (e) =>
  e?.code === "auth/popup-closed-by-user" || e?.code === "auth/cancelled-popup-request";

/* 로그인 실패는 원인이 제각각인데 화면에는 "못 했다" 하나로만 보인다.
   이 앱에서 실제로 두 번 발목을 잡혔다 — 팝업 차단 때 한 번, 이사 뒤 승인된
   도메인이 빠졌을 때 한 번. 둘 다 화면만 봐서는 구별이 안 됐다.
   아는 코드는 풀어 쓰고, 모르는 코드는 코드 그대로라도 보여준다.
   사내 도구라 코드가 노출돼서 잃을 것도 없다. */
const SIGN_IN_ERRORS = {
  "auth/unauthorized-domain": "이 주소가 승인된 도메인에 없습니다",
  "auth/operation-not-allowed": "Microsoft 로그인이 꺼져 있습니다",
  "auth/popup-blocked": "브라우저가 팝업을 막았습니다",
  "auth/invalid-credential": "Azure 앱 등록 설정이 맞지 않습니다",
  "auth/internal-error": "Azure 응답이 거부됐습니다 — 리디렉션 URI 를 확인해주세요",
};

export const signInError = (e) =>
  SIGN_IN_ERRORS[e?.code] ?? e?.code ?? e?.message ?? "원인 불명";

/* 에뮬레이터일 때만 알아서 로그인한다. 배포에서는 아무것도 하지 않는다 —
   거기서는 버튼을 눌러야 하고, 그게 팝업이 막히지 않는 유일한 방법이다.
   로컬 개발과 tools/e2e.py 가 로그인 버튼을 누르지 않아도 되는 이유이기도 하다.

   약속을 캐시하는 이유: StrictMode 가 이펙트를 두 번 돌리면 두 호출이 나란히
   "로그인 시도 → 실패 → 계정 생성" 을 밟아 한쪽이 EMAIL_EXISTS 로 떨어진다.
   결과는 같지만 콘솔에 400 이 쌓여서, e2e 의 콘솔 오류 검사가 진짜 문제와 구별하지
   못한다. */
let auto = null;
export const autoSignIn = () => (auto ??= USE_EMULATOR ? signIn() : Promise.resolve(null));
